<script
  setup
  lang="ts"
  generic="
    ListingFilter extends {
      code: string;
      min?: number;
      max?: number;
      label: string;
    }
  "
>
import { computed, reactive, watch } from "vue";

const emits = defineEmits<{
  (event: "select-value", value: { code: string; value: unknown }): void;
  (event: "update-visibility", flag: boolean): void;
}>();

const props = defineProps<{
  filter: ListingFilter;
  isFilterVisible: boolean;
  toggle: Function;
}>();

type Translations = {
  listing: {
    min: string;
    max: string;
  };
};
const translations: Translations = {
  listing: {
    min: "Min",
    max: "Max",
  },
};

const route = useRoute();

const toOptionalNumber = (value: unknown): number | undefined => {
  const normalized = Array.isArray(value) ? value[0] : value;
  if (normalized === null || normalized === undefined || normalized === "") {
    return undefined;
  }
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const bounds = computed(() => {
  const min = toOptionalNumber(props.filter?.min) ?? 0;
  const max = toOptionalNumber(props.filter?.max) ?? 0;
  return { min, max };
});

const prices = reactive<{ min: number; max: number }>({
  min: bounds.value.min,
  max: bounds.value.max,
});

const syncingFromRoute = ref(false);
const suppressNextEmit = reactive<{ min: boolean; max: boolean }>({
  min: false,
  max: false,
});

const syncPricesFromRoute = () => {
  const routeMin = toOptionalNumber(route.query["min-price"]);
  const routeMax = toOptionalNumber(route.query["max-price"]);
  const nextMin = routeMin ?? bounds.value.min;
  const nextMax = routeMax ?? bounds.value.max;

  syncingFromRoute.value = true;
  suppressNextEmit.min = prices.min !== nextMin;
  suppressNextEmit.max = prices.max !== nextMax;
  prices.min = nextMin;
  prices.max = nextMax;
  queueMicrotask(() => {
    syncingFromRoute.value = false;
  });
};

watch(
  () => [route.query["min-price"], route.query["max-price"], bounds.value.min, bounds.value.max],
  () => {
    syncPricesFromRoute();
  },
  { immediate: true },
);

function onMinPriceChange(newPrice: number, oldPrice: number) {
  if (suppressNextEmit.min) {
    suppressNextEmit.min = false;
    return;
  }
  if (syncingFromRoute.value || newPrice === oldPrice) return;
  emits("select-value", {
    code: "min-price",
    value: newPrice,
  });
}
const debounceMinPriceUpdate = useDebounceFn(onMinPriceChange, 500);
watch(() => prices.min, debounceMinPriceUpdate);

function onMaxPriceChange(newPrice: number, oldPrice: number) {
  if (suppressNextEmit.max) {
    suppressNextEmit.max = false;
    return;
  }
  if (syncingFromRoute.value || newPrice === oldPrice) return;
  emits("select-value", {
    code: "max-price",
    value: newPrice,
  });
}
const debounceMaxPriceUpdate = useDebounceFn(onMaxPriceChange, 500);
watch(() => prices.max, debounceMaxPriceUpdate);

const trackStyle = computed(() => {
  const range = Math.max(bounds.value.max - bounds.value.min, 1);
  const percent1 = ((prices.min - bounds.value.min) / range) * 100;
  const percent2 = ((prices.max - bounds.value.min) / range) * 100;
  return `linear-gradient(to right, #E5E7EB ${percent1}%, #676767 ${percent1}%, #676767 ${percent2}%, #E5E7EB ${percent2}%)`;
});
</script>

<template>
  <div 
  :class="{
    'active' : isFilterVisible
  }"
  class="border-b border-gray-200 collapse-item">
    <button
      @click="toggle($event)"
      class="py-4 flex bg-transparent border-0 w-full items-center justify-between text-left cursor-pointer">
      <span class="font-mulish font-semibold text-base 3xl:text-md max-w-[90%] text-grey-700"
      >{{ $t('filters.price') }}</span
      >
      <span class="accordion-icon">
        <i 
        :class="{
          'icon-minus': isFilterVisible,
          'icon-add': !isFilterVisible
        }"
        class="text-black transform transition-transform duration-200"></i>
      </span>
    </button>

    <Transition
      enter-active-class="transition-all duration-300 ease-in-out overflow-hidden"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-[240px] opacity-100"
      leave-active-class="transition-all duration-300 ease-in-out overflow-hidden"
      leave-from-class="max-h-[240px] opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div 
        v-if="isFilterVisible"
        :id="props.filter.code" 
        class="accordion-content overflow-hidden transition-all max-h-0 duration-300 pr-1"
        >
        
        <div class="accordian-inner pt-2 pb-6">
          <!-- Range Input Container -->
          <div class="relative">
            <div class="slider-track absolute w-full h-1 bg-grey-100 rounded-lg" :style="{ background: trackStyle }"></div>

            <!-- Min Range Input -->
            <input
              type="range"
              id="min-price"
              v-model.number="prices.min"
              name="min-price"
              :min="bounds.min"
              :max="bounds.max"
              class="!absolute mt-[1px] !w-full !h-1 !bg-transparent !appearance-none !pointer-events-none 
              [&::-webkit-slider-thumb]:!pointer-events-auto
              [&::-webkit-slider-thumb]:!w-4
              [&::-webkit-slider-thumb]:!h-4
              [&::-webkit-slider-thumb]:!appearance-none
              [&::-webkit-slider-thumb]:!rounded-full
              [&::-webkit-slider-thumb]:!bg-white
              [&::-webkit-slider-thumb]:!border-[1px]
              [&::-webkit-slider-thumb]:!border-solid
              [&::-webkit-slider-thumb]:!border-[#D1D5DB]
              [&::-webkit-slider-thumb]:!cursor-pointer
              [&::-moz-range-thumb]:!pointer-events-auto
              [&::-moz-range-thumb]:!w-4
              [&::-moz-range-thumb]:!h-4
              [&::-moz-range-thumb]:!appearance-none
              [&::-moz-range-thumb]:!rounded-full
              [&::-moz-range-thumb]:!bg-white
              [&::-moz-range-thumb]:!border-[1px]
              [&::-moz-range-thumb]:!border-solid
              [&::-moz-range-thumb]:!border-[#D1D5DB]
              [&::-moz-range-thumb]:!cursor-pointer" />

            <!-- Max Range Input -->
            <input
              type="range"
              id="max-price"
              v-model.number="prices.max"
              name="max-price"
              :min="bounds.min"
              :max="bounds.max"
              class="!absolute mt-[1px] !w-full !h-1 !bg-transparent !appearance-none !pointer-events-none 
              [&::-webkit-slider-thumb]:!pointer-events-auto
              [&::-webkit-slider-thumb]:!w-4
              [&::-webkit-slider-thumb]:!h-4
              [&::-webkit-slider-thumb]:!appearance-none
              [&::-webkit-slider-thumb]:!rounded-full
              [&::-webkit-slider-thumb]:!bg-white
              [&::-webkit-slider-thumb]:!border-[1px]
              [&::-webkit-slider-thumb]:!border-solid
              [&::-webkit-slider-thumb]:!border-[#D1D5DB]
              [&::-webkit-slider-thumb]:!cursor-pointer
              [&::-moz-range-thumb]:!pointer-events-auto
              [&::-moz-range-thumb]:!w-4
              [&::-moz-range-thumb]:!h-4
              [&::-moz-range-thumb]:!appearance-none
              [&::-moz-range-thumb]:!rounded-full
              [&::-moz-range-thumb]:!bg-white
              [&::-moz-range-thumb]:!border-[1px]
              [&::-moz-range-thumb]:!border-solid
              [&::-moz-range-thumb]:!border-[#D1D5DB]
              [&::-moz-range-thumb]:!cursor-pointer" />
          </div>

          <!-- Price Display -->
          <div class="mt-6 text-sm lg:text-base text-grey-500">
            {{ $t('filters.from') }} <span id="min-price-display" class="font-bold text-grey-700">{{ prices.min?.toString() }}</span> € 
            {{ $t('filters.to') }}
            <span id="max-price-display" class="font-bold text-grey-700">{{ prices.max?.toString() }}</span> €
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>